import { OrgUserModel, UserId } from '@cibo/core'
import { usePaginatedOrgUsers } from '@cibo/profile'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  AutocompleteRenderInputParams,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { Autocomplete } from 'formik-mui'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { object } from 'yup'

import { useSnackbar } from 'notistack'
import {
  ResponsiveDialog,
  ResponsiveDialogProps,
  UserSelectionDisplay,
} from '../../../consts/Theme'
import { useCreateFieldsErrorReportForUser } from '../queries'

export const CreateErrorReportDialog = ({
  userId,
  ...modalProps
}: ResponsiveDialogProps & { userId?: UserId }) => {
  const { t } = useTranslation('@cibo/impact/CreateErrorReportDialog')
  const [query, setQuery] = useState<string>()
  const { enqueueSnackbar } = useSnackbar()
  const userQuery = usePaginatedOrgUsers({ fullText: query, limit: 30, persona: 'grower' })
  const users = useMemo(() => userQuery.data?.items.map(user => new OrgUserModel(user)), [
    userQuery.dataUpdatedAt,
  ])
  const createErrorReport = useCreateFieldsErrorReportForUser()

  const validationSchema = useCallback(
    () =>
      object().shape({
        user: object()
          .required(t('userRequired'))
          .typeError(t('userRequired')),
      }),
    []
  )

  const handleSubmit = (values: { user: OrgUserModel }) =>
    createErrorReport.mutateAsync({ userId: values.user.userId }).then(() => {
      enqueueSnackbar(t('success'), { variant: 'success' })
      modalProps.onClose?.()
      setQuery(undefined)
    })

  const selectedUser = users?.find(({ user }) => `${user.userId}` === `${userId}`)

  return (
    <Formik
      enableReinitialize
      initialValues={{
        user: selectedUser || ((undefined as unknown) as OrgUserModel),
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ errors, handleReset, handleSubmit, isSubmitting, isValid, touched }) => {
        return (
          <Form>
            <ResponsiveDialog {...modalProps} maxWidth="xs" fullWidth>
              <DialogTitle>{t('createErrorReportTitle')}</DialogTitle>
              <DialogContent>
                <Stack spacing={2}>
                  <Typography color="text.secondary" whiteSpace={'pre-line'} variant="body2">
                    {t('createErrorReportBody')}
                  </Typography>

                  <Field
                    name="user"
                    component={Autocomplete}
                    options={users ?? []}
                    loading={userQuery.isLoading}
                    isOptionEqualToValue={(option: OrgUserModel, value: UserId) =>
                      option.userId === value
                    }
                    getOptionLabel={(option: OrgUserModel) => option.displayNameFull}
                    renderOption={(props: any, option: OrgUserModel) => (
                      <UserSelectionDisplay
                        data-testid={`new-error-report-user:${option.userId}`}
                        option={option}
                        {...props}
                      />
                    )}
                    style={{ width: 300 }}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                      <TextField
                        {...params}
                        // We have to manually set the corresponding fields on the input component
                        name="user"
                        error={touched['user'] && !!errors['user']}
                        helperText={errors['user']}
                        label={t('user')}
                        variant="outlined"
                        onChange={e => setQuery(e.target.value)}
                        inputProps={{
                          ...params.inputProps,
                          'data-testid': 'new-error-report-user-picker-input',
                        }}
                      />
                    )}
                    data-testid="new-error-report-user-picker"
                    disabled={!!selectedUser}
                  />
                  <Collapse in={createErrorReport.isError}>
                    <Fade in={createErrorReport.isError}>
                      <Alert
                        severity="error"
                        sx={{ whiteSpace: 'pre-line' }}
                        data-testid="new-error-report-failed-message"
                      >
                        {createErrorReport.error?.response?.data?.message
                          ? t('error_message', {
                              message: createErrorReport.error.response.data.message,
                            })
                          : t('error')}
                      </Alert>
                    </Fade>
                  </Collapse>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    modalProps.onClose()
                    createErrorReport.reset()
                    handleReset()
                  }}
                >
                  {t('cancel')}
                </Button>
                <LoadingButton
                  loading={isSubmitting}
                  disabled={!isValid}
                  onClick={() => handleSubmit()}
                  variant="contained"
                  data-testid="submit-new-error-report"
                >
                  {t('create')}
                </LoadingButton>
              </DialogActions>
            </ResponsiveDialog>
          </Form>
        )
      }}
    </Formik>
  )
}
